/*
 * CompoundChipCard — Slice 4 sharp-fill chip batching, smart auto-advance.
 *
 * Renders the primary next-question + 1-3 adjacent same-section chip
 * siblings as ONE card with multiple labeled chip rows. The card guides
 * the user one row at a time: pick an option on the active row → the
 * row collapses to "answered" (dim + check + picked value), the next
 * pending row gets highlighted + auto-scrolled into view. After the
 * last row is answered/skipped, the card auto-submits — no "Save all"
 * button at the end. The UI knows when it's done.
 *
 * Row modes:
 *   - 'answered'  — picked or skipped; dimmed; shows ✓ + chosen value;
 *                   tap to reactivate (change answer)
 *   - 'active'    — the row the user should answer next; chips visible;
 *                   row gets a subtle highlight ring
 *   - 'pending'   — future rows; faded; chips dimmed/inactive (still
 *                   tappable but visually de-emphasized)
 *
 * Multiselect rows can't auto-advance (the user is mid-pick); they
 * carry an inline "Next" button that fires when ready.
 *
 * Brand:
 *   - open canvas (no card surface — inherits TurnPane breathing room)
 *   - active row: rgba(24,25,29,0.05) tint, soft 12px radius
 *   - answered row: rgba(24,25,29,0.025) + 0.55 opacity
 *   - pending row: 0.45 opacity, chips muted
 *   - Plex Mono caps eyebrow on intro; sentence case throughout
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, X } from 'lucide-react';

import type {
  BatchConfirmAction,
  BatchConfirmResponse,
  CompoundChipsPayload,
  NextQuestion,
} from '../../lib/dossier-types';
import { confirmBatch } from '../../lib/intakeApi';


type RowSelection =
  | { kind: 'unset' }
  | { kind: 'single'; value: string }
  | { kind: 'multi'; values: string[] }
  | { kind: 'skipped' };


export interface CompoundChipCardProps {
  payload: CompoundChipsPayload;
  clientId?: string;
  layer?: 'L1' | 'L2';
  onSubmitted: (result: BatchConfirmResponse) => void;
  disabled?: boolean;
}


/** Returns true when a row has an answer the user committed to —
 *  picked a single value, picked at least one multi value, or
 *  explicitly skipped it. Unset rows are NOT considered touched. */
function isRowTouched(sel: RowSelection): boolean {
  if (sel.kind === 'single') return true;
  if (sel.kind === 'skipped') return true;
  if (sel.kind === 'multi') return sel.values.length > 0;
  return false;
}


/** Compute the index of the next pending row (not yet touched).
 *  Returns -1 when every row is touched — auto-submit fires from this. */
function nextPendingIdx(
  fields: NextQuestion[],
  selections: Record<string, RowSelection>,
  startFrom: number,
): number {
  for (let i = startFrom; i < fields.length; i += 1) {
    const sel = selections[fields[i].field] ?? { kind: 'unset' as const };
    if (!isRowTouched(sel)) return i;
  }
  return -1;
}


export const CompoundChipCard: React.FC<CompoundChipCardProps> = ({
  payload,
  clientId,
  layer,
  onSubmitted,
  disabled,
}) => {
  const [selections, setSelections] = useState<Record<string, RowSelection>>(() => {
    const init: Record<string, RowSelection> = {};
    for (const f of payload.fields) {
      init[f.field] = f.kind === 'multiselect'
        ? { kind: 'multi', values: [] }
        : { kind: 'unset' };
    }
    return init;
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Refs per row for scrollIntoView when we auto-advance. Allocated
  // up front so the indices stay stable across renders.
  const rowRefs = useRef<Array<HTMLLIElement | null>>([]);
  rowRefs.current = useMemo(
    () => new Array(payload.fields.length).fill(null),
    [payload.fields.length],
  );

  const allTouched = useMemo(
    () => payload.fields.every((f) => isRowTouched(selections[f.field] ?? { kind: 'unset' })),
    [payload.fields, selections],
  );

  const submit = useCallback(async () => {
    if (submitting || done) return;
    setSubmitError(null);
    const confirmations: BatchConfirmAction[] = [];
    for (const f of payload.fields) {
      const s = selections[f.field];
      if (!s) continue;
      if (s.kind === 'skipped') {
        confirmations.push({ field: f.field, action: 'skip' });
        continue;
      }
      if (s.kind === 'single') {
        confirmations.push({ field: f.field, action: 'edit', value: s.value });
        continue;
      }
      if (s.kind === 'multi' && s.values.length > 0) {
        confirmations.push({ field: f.field, action: 'edit', value: s.values });
      }
    }
    if (confirmations.length === 0) {
      setSubmitError('Pick at least one answer or skip a row.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await confirmBatch(confirmations, clientId, layer);
      setDone(true);
      onSubmitted(res);
    } catch (e) {
      console.error('[CompoundChipCard] submit failed', e);
      setSubmitError("Something went wrong saving. Try again?");
    } finally {
      setSubmitting(false);
    }
  }, [submitting, done, payload.fields, selections, clientId, layer, onSubmitted]);

  // Auto-submit when every row has been touched. The slight delay lets
  // the user SEE their last pick land (the answered-state transition)
  // before the card disappears.
  useEffect(() => {
    if (allTouched && !submitting && !done && !submitError) {
      const handle = window.setTimeout(() => {
        void submit();
      }, 480);
      return () => window.clearTimeout(handle);
    }
    return undefined;
  }, [allTouched, submitting, done, submitError, submit]);

  /** Move the active marker + smooth-scroll the row into view. */
  const advanceTo = useCallback((idx: number) => {
    setActiveIdx(idx);
    // Defer the scroll a frame so the row's DOM has its new "active"
    // class applied (some browsers center based on layout, not class).
    window.requestAnimationFrame(() => {
      const node = rowRefs.current[idx];
      if (node && typeof node.scrollIntoView === 'function') {
        try {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
          // Older browsers: fall back to instant scroll.
          node.scrollIntoView();
        }
      }
    });
  }, []);

  // Auto-advance effect — the single source of truth for moving the
  // active marker forward. When the ACTIVE row becomes touched (a single
  // pick or a skip), advance to the next pending row after a brief beat
  // so the user sees their pick land. We do NOT call setActiveIdx inside
  // a setSelections updater (that anti-pattern silently dropped the
  // advance, leaving the next row stuck in its faded "pending" state).
  //
  // Multiselect is excluded while mid-pick (has values, not skipped) —
  // the user drives those with the inline "Next" button so they can
  // pick more than one option before moving on.
  useEffect(() => {
    if (submitting || done) return;
    const f = payload.fields[activeIdx];
    if (!f) return;
    const sel = selections[f.field];
    if (!sel || !isRowTouched(sel)) return;
    if (f.kind === 'multiselect' && sel.kind === 'multi' && sel.values.length > 0) {
      return; // mid multi-pick — wait for the Next button
    }
    const next = nextPendingIdx(payload.fields, selections, 0);
    if (next === -1 || next === activeIdx) return; // all done → submit effect fires
    const h = window.setTimeout(() => advanceTo(next), 260);
    return () => window.clearTimeout(h);
  }, [selections, activeIdx, submitting, done, payload.fields, advanceTo]);

  const handleChipPick = (field: NextQuestion, fieldIdx: number, value: string) => {
    setSelections(prev => {
      const cur = prev[field.field];
      if (field.kind === 'multiselect') {
        const arr = cur?.kind === 'multi' ? cur.values : [];
        const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
        return { ...prev, [field.field]: { kind: 'multi', values: next } };
      }
      return { ...prev, [field.field]: { kind: 'single', value } };
    });
    // Auto-advance is handled by the effect above (watches selections).
  };

  const handleSkipRow = (field: NextQuestion, fieldIdx: number) => {
    setSelections(prev => {
      const cur = prev[field.field];
      const skipped = cur?.kind === 'skipped';
      if (skipped) {
        // Un-skip: revert to empty + reactivate this row so user can pick.
        const reset: RowSelection = field.kind === 'multiselect'
          ? { kind: 'multi', values: [] }
          : { kind: 'unset' };
        return { ...prev, [field.field]: reset };
      }
      return { ...prev, [field.field]: { kind: 'skipped' } };
    });
    // Skipping is a "touch" — the auto-advance effect moves us forward.
  };

  /** Multiselect rows: explicit "Next" button to advance once the user
   *  is done picking. The active row's values are already in `selections`
   *  so we compute the next pending row directly (no updater side-effect). */
  const handleMultiNext = (fieldIdx: number) => {
    const next = nextPendingIdx(payload.fields, selections, fieldIdx + 1);
    if (next === -1) {
      // Last row done — the allTouched submit effect takes over once
      // this row's values count as touched (they already do).
      return;
    }
    advanceTo(next);
  };

  /** Tap on an answered row → re-activate it for change. */
  const handleReactivateRow = (fieldIdx: number, field: NextQuestion) => {
    setSelections(prev => ({
      ...prev,
      [field.field]: field.kind === 'multiselect'
        ? { kind: 'multi', values: [] }
        : { kind: 'unset' },
    }));
    setActiveIdx(fieldIdx);
  };

  const touchedCount = useMemo(
    () => payload.fields.filter(f => isRowTouched(selections[f.field] ?? { kind: 'unset' })).length,
    [payload.fields, selections],
  );

  if (done) {
    return (
      <div className="aa-compound-chips aa-compound-chips--done">
        <p className="aa-compound-chips-done-line">
          Saved {touchedCount} of {payload.fields.length}.
        </p>
      </div>
    );
  }

  // Orbit narrates before the form — sharp 1-line + why.
  const narrationLine = payload.intro
    ?? `${payload.fields.length} quick ones — same area, faster together.`;
  const narrationWhy = payload.why
    ?? `Each one tells me a different slice. Skip any that don't fit.`;

  return (
    <div className="aa-compound-chips">
      <header className="aa-compound-chips-narration">
        <h3 className="aa-compound-chips-narration-line">{narrationLine}</h3>
        <p className="aa-compound-chips-narration-why">{narrationWhy}</p>
      </header>

      <ul className="aa-compound-chips-rows" role="list">
        {payload.fields.map((f, i) => {
          const sel = selections[f.field] ?? { kind: 'unset' as const };
          const touched = isRowTouched(sel);
          const isAnswered = touched;
          // EVERY unanswered row is interactive — answer them in any
          // order. `isActive` is now ONLY a scroll/visual hint (which
          // row to glide to on advance); it NEVER gates clickability.
          // This kills the "stuck because activeIdx didn't advance"
          // class of bugs — a row can't become un-clickable.
          const isActive = i === activeIdx && !touched;
          const mode = isAnswered ? 'answered' : (isActive ? 'active' : 'open');

          // Derive the human-readable answer for the answered-row preview.
          let answeredText = '';
          if (sel.kind === 'skipped') answeredText = 'Skipped';
          else if (sel.kind === 'single') {
            const opt = (f.chips ?? []).find(c => c.value === sel.value);
            answeredText = opt?.label ?? sel.value;
          } else if (sel.kind === 'multi' && sel.values.length > 0) {
            answeredText = sel.values
              .map(v => (f.chips ?? []).find(c => c.value === v)?.label ?? v)
              .join(', ');
          }

          return (
            <li
              key={f.field}
              ref={(el) => { rowRefs.current[i] = el; }}
              className={
                'aa-compound-chips-row'
                + ` aa-compound-chips-row--${mode}`
                + (sel.kind === 'skipped' ? ' aa-compound-chips-row--skipped' : '')
              }
            >
              {/* ANSWERED MODE — collapsed preview, tap to reactivate */}
              {isAnswered ? (
                <button
                  type="button"
                  className="aa-compound-chips-row-answered"
                  onClick={() => handleReactivateRow(i, f)}
                  disabled={submitting}
                  aria-label={`${f.label}: ${answeredText}. Tap to change.`}
                >
                  <span className="aa-compound-chips-row-check">
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className="aa-compound-chips-row-answered-body">
                    <span className="aa-compound-chips-row-answered-label">
                      {f.prompt || f.label}
                    </span>
                    <span className="aa-compound-chips-row-answered-value">
                      {answeredText}
                    </span>
                  </span>
                  <span className="aa-compound-chips-row-answered-change">
                    Change
                  </span>
                </button>
              ) : (
                <>
                  <div className="aa-compound-chips-row-head">
                    <div className="aa-compound-chips-row-narration">
                      <span className="aa-compound-chips-row-label">
                        {f.prompt || f.label}
                      </span>
                      {f.why && (
                        <span className="aa-compound-chips-row-why">{f.why}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="aa-compound-chips-row-skip"
                      onClick={() => handleSkipRow(f, i)}
                      disabled={submitting}
                      aria-label={`Skip ${f.label}`}
                      title="Skip this one"
                    >
                      <X size={11} strokeWidth={2.25} />
                    </button>
                  </div>
                  <div className="aa-compound-chips-row-chips">
                    {(f.chips ?? []).map((opt) => {
                      const v = opt.value;
                      const isPicked = sel.kind === 'single'
                        ? sel.value === v
                        : sel.kind === 'multi'
                          ? sel.values.includes(v)
                          : false;
                      return (
                        <button
                          key={v}
                          type="button"
                          className={
                            'aa-compound-chips-chip'
                            + (isPicked ? ' aa-compound-chips-chip--on' : '')
                          }
                          onClick={() => handleChipPick(f, i, v)}
                          disabled={submitting}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Multiselect rows get an inline Next button to advance
                      once the user has picked at least one option. */}
                  {f.kind === 'multiselect'
                    && sel.kind === 'multi' && sel.values.length > 0 && (
                    <div className="aa-compound-chips-row-next">
                      <button
                        type="button"
                        className="aa-compound-chips-row-next-btn"
                        onClick={() => handleMultiNext(i)}
                        disabled={submitting}
                      >
                        <span>Next</span>
                        <ArrowRight size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {submitError && (
        <p className="aa-compound-chips-error">{submitError}</p>
      )}

      {submitting && !submitError && (
        <p className="aa-compound-chips-saving">Saving…</p>
      )}

      {/* Guaranteed submit. Auto-submit still fires once every row is
       *  touched, but this button is ALWAYS available the moment the
       *  user has answered at least one row — so the flow can never
       *  dead-end if auto-advance/auto-submit hiccups. Hidden only
       *  while submitting or before any answer. */}
      {!submitting && touchedCount > 0 && (
        <footer className="aa-compound-chips-footer">
          <button
            type="button"
            className="aa-compound-chips-primary"
            onClick={() => void submit()}
            disabled={submitting}
          >
            <Check size={13} strokeWidth={2.5} />
            <span>
              {touchedCount === payload.fields.length
                ? 'Save all'
                : `Save ${touchedCount} of ${payload.fields.length}`}
            </span>
          </button>
        </footer>
      )}
    </div>
  );
};

export default CompoundChipCard;
